import { useState, useCallback, useMemo } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { useIntl } from 'react-intl';

export const useGoogleGeocoding = () => {
  const intl = useIntl();

  const sanitizeLanguage = (rawLang: string): string => {
    if (!rawLang) return 'pt-BR';
    
    const normalized = rawLang.toUpperCase();
    
    const map: Record<string, string> = {
      'EN': 'en-us',
      'DE': 'de-de',
      'FR': 'fr-ca',
      'PT': 'pt-br',
      'ES': 'es-es',
      'RU': 'ru-ru',
      'PL': 'pl-pl',
      'EL': 'el-gr',
      'ZH': 'zh-zh',
      'IT': 'it-it',
      'HU': 'hu-hu',
      'CS': 'cs-cs',
      'DA': 'da-dk',
      'FI': 'fi-fi',
      'NO': 'no-no',
      'SV': 'sv-se'
    };

    return map[normalized] || rawLang.toLowerCase();
  };

  const googleLanguage = useMemo(() => {
    return sanitizeLanguage(intl.locale);
  }, [intl.locale]);

  const { isLoaded, loadError } = useLoadScript({
    id: 'script-loader',
    libraries: [],
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    language: googleLanguage,
  });

  const [isLoading, setIsLoading] = useState(false);

  const getCoordinates = useCallback(async (address: string) => {
    if (!isLoaded) {
      throw new Error('Google Maps API ainda não carregou');
    }

    if (loadError) {
      throw new Error('Erro ao carregar Google Maps API');
    }

    setIsLoading(true);

    return new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      const timeoutId = setTimeout(() => {
        setIsLoading(false);
        reject(new Error('Geocode request timed out (Google API not responding)'));
      }, 5000);

      geocoder.geocode({ address: address }, (results, status) => {
        clearTimeout(timeoutId);
        setIsLoading(false);

        if (status === 'OK' && results) {
          resolve(results);
        } else {
          const errorMessage = status === 'ZERO_RESULTS' 
            ? 'Endereço não encontrado' 
            : `Erro na geocodificação: ${status}`;
          reject(new Error(errorMessage));
        }
      });
    });
  }, [isLoaded, loadError]);

  return {
    getCoordinates,
    isLoaded,
    loadError,
    isLoading
  };
};